import React, { useState } from 'react';
import { Activity, RefreshCw, X, Server, Cpu, HardDrive, ShieldCheck, AlertTriangle, Layers } from 'lucide-react';

export default function MeshScanModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(false);
  const [scanData, setScanData] = useState(null);
  const [error, setError] = useState(null);

  const runScan = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/system/scan');
      const json = await res.json();
      if (json.success) {
        setScanData(json.data);
      } else {
        setError(json.error || 'Failed to complete scan');
      }
    } catch (err) {
      setError(err.message || 'Network error executing scan');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-md p-4 overflow-y-auto">
      <div className="bg-dark-900 border border-dark-700 rounded-2xl max-w-4xl w-full p-6 text-white shadow-2xl space-y-6 max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between border-b border-dark-700 pb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-teal-500/10 border border-teal-500/30 rounded-xl text-teal-400">
              <Activity className="w-6 h-6 animate-pulse" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                Tailnet Mesh Ecosystem Scanner
              </h2>
              <p className="text-xs text-dark-400">Real-time device CPU/Memory load, Docker containers & processes</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={runScan}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-400 hover:to-emerald-500 text-white font-medium rounded-xl text-sm transition-all shadow-lg hover:shadow-teal-500/20 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Scanning Mesh...' : 'Run Real-Time Scan'}
            </button>
            
            <button
              onClick={onClose}
              className="p-2 text-dark-400 hover:text-white hover:bg-dark-800 rounded-xl transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content Body */}
        {error && (
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3 text-red-400 text-sm">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!scanData && !loading && !error && (
          <div className="py-12 text-center space-y-4">
            <Server className="w-12 h-12 text-dark-500 mx-auto" />
            <h3 className="text-lg font-medium text-dark-300">No Scan Results Yet</h3>
            <p className="text-sm text-dark-400 max-w-md mx-auto">
              Click the <strong className="text-teal-400">Run Real-Time Scan</strong> button above to audit CPU/Memory usage, active Docker containers, and processes across all 3 nodes.
            </p>
          </div>
        )}

        {loading && (
          <div className="py-16 text-center space-y-4">
            <RefreshCw className="w-10 h-10 text-teal-400 animate-spin mx-auto" />
            <p className="text-sm text-teal-300 font-medium">Communicating with Tailnet nodes (dios-macbook-air, macmini-primary, macmini-secondary)...</p>
          </div>
        )}

        {scanData && !loading && (
          <div className="space-y-6">
            
            {/* Top Metrics Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 bg-dark-800/60 border border-dark-700 rounded-xl">
                <div className="text-xs text-dark-400 mb-1">Nodes Scanned</div>
                <div className="text-2xl font-bold text-teal-400">
                  {scanData.online_nodes_count} / {scanData.nodes_scanned} Online
                </div>
              </div>
              <div className="p-4 bg-dark-800/60 border border-dark-700 rounded-xl">
                <div className="text-xs text-dark-400 mb-1">Total Active Containers</div>
                <div className="text-2xl font-bold text-emerald-400">
                  {scanData.total_containers_running} Running
                </div>
              </div>
              <div className="p-4 bg-dark-800/60 border border-dark-700 rounded-xl">
                <div className="text-xs text-dark-400 mb-1">Last Scan Timestamp</div>
                <div className="text-sm font-mono text-dark-300 mt-2">
                  {new Date(scanData.timestamp).toLocaleTimeString()}
                </div>
              </div>
            </div>

            {/* Nodes Breakdown */}
            <div className="space-y-4">
              {scanData.nodes.map((node) => (
                <div key={node.node_id} className="p-5 bg-dark-800/40 border border-dark-700 rounded-xl space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${node.status === 'ONLINE' ? 'bg-emerald-400 shadow-lg shadow-emerald-500/50' : 'bg-red-500'}`} />
                      <h4 className="font-semibold text-white">{node.name}</h4>
                      <span className="text-xs font-mono text-dark-400">({node.ip})</span>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${node.status === 'ONLINE' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`}>
                      {node.status}
                    </span>
                  </div>

                  {node.status === 'ONLINE' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                      
                      {/* Load & Memory */}
                      <div className="p-3 bg-dark-900/60 rounded-lg space-y-2 text-xs">
                        <div className="flex items-center justify-between text-dark-300">
                          <span className="flex items-center gap-1.5"><Cpu className="w-3.5 h-3.5 text-teal-400" /> CPU Usage</span>
                          <span className="font-bold text-teal-400">{node.cpu_usage_pct}%</span>
                        </div>
                        <div className="w-full bg-dark-700 h-2 rounded-full overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-teal-500 to-emerald-400 h-full transition-all"
                            style={{ width: `${Math.min(node.cpu_usage_pct, 100)}%` }}
                          />
                        </div>
                        <div className="text-dark-400 pt-1">
                          <span className="font-semibold text-dark-300">Uptime / Load:</span> {node.load_average}
                        </div>
                        <div className="text-dark-400">
                          <span className="font-semibold text-dark-300">Memory:</span> {node.memory_info}
                        </div>
                      </div>

                      {/* Docker Containers */}
                      <div className="p-3 bg-dark-900/60 rounded-lg text-xs space-y-2">
                        <div className="flex items-center justify-between text-dark-300 font-semibold mb-1">
                          <span className="flex items-center gap-1.5"><Layers className="w-3.5 h-3.5 text-emerald-400" /> Active Containers ({node.containers.length})</span>
                        </div>
                        {node.containers.length === 0 ? (
                          <div className="text-dark-500 italic">No Docker containers on this node</div>
                        ) : (
                          <div className="max-h-24 overflow-y-auto space-y-1">
                            {node.containers.map((c, idx) => (
                              <div key={idx} className="flex items-center justify-between text-dark-300 bg-dark-800/80 px-2 py-1 rounded border border-dark-700/50">
                                <span className="font-mono text-emerald-300 font-semibold">{c.name}</span>
                                <span className="text-[10px] text-dark-400">{c.status}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                    </div>
                  )}
                </div>
              ))}
            </div>

          </div>
        )}

      </div>
    </div>
  );
}
